/** Client-side fallbacks when hosted by allworld (static R2, no project Worker API). */

const DEMO_CREDENTIALS = {
  'admin@softwarelink.net': {
    password: 'Admin@2026!Sec',
    user: {
      id: 'usr_admin_01',
      email: 'admin@softwarelink.net',
      displayName: '安大理化试验室主任 (Admin)',
      role: 'LAB_ENGINEER',
      organizationType: 'DOMESTIC',
    },
  },
  'auditor@softwarelink.net': {
    password: 'Auditor@2026!Sec',
    user: {
      id: 'usr_audit_01',
      email: 'auditor@softwarelink.net',
      displayName: '机电办国际合规审计员',
      role: 'AUDITOR',
      organizationType: 'DOMESTIC',
    },
  },
}

function encodeSession(user) {
  const payload = btoa(unescape(encodeURIComponent(JSON.stringify({
    ...user,
    exp: Date.now() + 12 * 60 * 60 * 1000,
  }))))
  return `fb.${payload}`
}

function decodeSession(token) {
  if (!token || !token.startsWith('fb.')) return null
  try {
    const raw = decodeURIComponent(escape(atob(token.slice(3))))
    const data = JSON.parse(raw)
    if (!data.exp || data.exp < Date.now()) return null
    return {
      id: data.id,
      email: data.email,
      displayName: data.displayName,
      role: data.role,
      organizationType: data.organizationType,
    }
  } catch {
    return null
  }
}

function countdown(targetMs) {
  const diff = Math.max(0, targetMs - Date.now())
  return {
    expired: diff === 0,
    remainingMs: diff,
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
    seconds: Math.floor((diff % 60000) / 1000),
  }
}

function parseDeadline(isoLike) {
  const normalized = isoLike.includes('T') ? isoLike : `${isoLike.replace(' ', 'T')}+08:00`
  return new Date(normalized).getTime()
}

export function computeDualAxisWaveform(input) {
  const samples = Math.min(Math.max(input.samples ?? 360, 64), 2048)
  const durationSec = input.durationSec ?? 1
  const omega = 2 * Math.PI * input.frequencyHz
  const phi = (input.phaseShiftDeg * Math.PI) / 180
  const F0 = input.axialLoadKn
  const M0 = input.torsionTorqueNm
  const t = []
  const axial = []
  const torsion = []
  const lissajous = []
  for (let i = 0; i < samples; i++) {
    const ti = (i / samples) * durationSec
    const fz = F0 * Math.sin(omega * ti)
    const mz = M0 * Math.sin(omega * ti + phi)
    t.push(ti)
    axial.push(Number(fz.toFixed(4)))
    torsion.push(Number(mz.toFixed(4)))
    lissajous.push({ x: Number(fz.toFixed(4)), y: Number(mz.toFixed(4)) })
  }
  const phaseCoupling = Math.abs(Math.sin(phi))
  const thermalFactor = 1 + Math.max(0, input.tempCelsius - 20) / 1200
  const vonMisesProxy = Math.sqrt(F0 * F0 + 3 * (M0 / 100) * (M0 / 100)) * thermalFactor
  const damageIndex = Number((vonMisesProxy * (0.55 + 0.45 * phaseCoupling) * Math.sqrt(input.frequencyHz)).toFixed(3))
  return {
    equations: {
      axial: 'Fz = F0 · sin(ωt)',
      torsion: 'Mz = M0 · sin(ωt + φ)',
      omega: `ω = 2πf = ${omega.toFixed(4)} rad/s`,
      phiRad: Number(phi.toFixed(6)),
    },
    metrics: {
      vonMisesProxy: Number(vonMisesProxy.toFixed(3)),
      damageIndex,
      phaseCoupling: Number(phaseCoupling.toFixed(4)),
      thermalFactor: Number(thermalFactor.toFixed(4)),
      ellipseEccentricity: Number((1 - Math.min(F0, M0 / 10) / Math.max(F0, M0 / 10, 1e-6)).toFixed(4)),
    },
    series: { t, axial, torsion, lissajous },
  }
}

export function evaluateQualification(input) {
  const axes = {
    manufacturerAuth: input.hasManufacturerAuth ? 100 : 0,
    bankCredit: input.hasBankCreditCert
      ? Math.min(100, 60 + (input.bankCertMonths && input.bankCertMonths >= 3 ? 40 : 0))
      : 0,
    foreignTrade: input.bidderType === 'OVERSEAS'
      ? (input.hasForeignTradeContractCapacity ? 100 : 0)
      : 85,
    creditIntegrity: input.hasCreditChinaViolation ? 0 : 100,
    documentCompleteness: [
      input.hasManufacturerAuth,
      input.hasBankCreditCert,
      input.bidderType === 'DOMESTIC' || input.hasForeignTradeContractCapacity,
      !input.hasCreditChinaViolation,
    ].filter(Boolean).length * 25,
  }
  let score =
    axes.manufacturerAuth * 0.25 +
    axes.bankCredit * 0.25 +
    axes.foreignTrade * 0.2 +
    axes.creditIntegrity * 0.2 +
    axes.documentCompleteness * 0.1
  const risks = []
  if (!input.hasManufacturerAuth) risks.push('缺少制造商授权 / 原装同源证明')
  if (!input.hasBankCreditCert) risks.push('缺少近3个月银行资信证明')
  if (input.bidderType === 'OVERSEAS' && !input.hasForeignTradeContractCapacity) {
    risks.push('关境外主体不具备外贸合同签署与合规收汇能力')
  }
  if (input.hasCreditChinaViolation) risks.push('命中失信被执行人 / 信用中国风险标记')
  if (input.hasCreditChinaViolation) score = Math.min(score, 35)
  const verdict = score >= 85 ? 'PASS' : score >= 60 ? 'CONDITIONAL' : 'FAIL'
  return {
    complianceScore: Number(score.toFixed(2)),
    verdict,
    axes,
    risks,
    radar: [
      { axis: '制造商授权', value: axes.manufacturerAuth },
      { axis: '银行资信', value: axes.bankCredit },
      { axis: '外贸履约', value: axes.foreignTrade },
      { axis: '信用完整性', value: axes.creditIntegrity },
      { axis: '要件完备度', value: axes.documentCompleteness },
    ],
  }
}

const QUAL_STORE_KEY = 'fatigue_bid_qual_records'
const CONFIG_STORE_KEY = 'fatigue_bid_configs'
const AUDIT_STORE_KEY = 'fatigue_bid_audits'

function loadJson(key, fallback) {
  try {
    return JSON.parse(localStorage.getItem(key) || 'null') ?? fallback
  } catch {
    return fallback
  }
}

function saveJson(key, value) {
  localStorage.setItem(key, JSON.stringify(value))
}

function pushAudit(userId, action, payload) {
  const logs = loadJson(AUDIT_STORE_KEY, [])
  logs.unshift({
    id: logs.length + 1,
    user_id: userId,
    action,
    ip_address: 'local',
    payload: JSON.stringify(payload ?? {}),
    timestamp: new Date().toISOString(),
  })
  saveJson(AUDIT_STORE_KEY, logs.slice(0, 200))
}

const PRESETS = [
  {
    id: 'sim_gh4169_01',
    preset_name: 'GH4169高温合金涡轮盘多轴疲劳基准',
    material_grade: 'Inconel 718 / GH4169',
    temp_celsius: 650,
    axial_load_kn: 50,
    torsion_torque_nm: 500,
    frequency_hz: 10,
    phase_shift_deg: 90,
  },
  {
    id: 'sim_dd6_02',
    preset_name: 'DD6单晶叶片拉扭复合高周疲劳',
    material_grade: 'DD6 Single Crystal',
    temp_celsius: 980,
    axial_load_kn: 25,
    torsion_torque_nm: 250,
    frequency_hz: 20,
    phase_shift_deg: 0,
  },
]

function getBearer(headers) {
  const h = headers?.Authorization || ''
  return h.startsWith('Bearer ') ? h.slice(7).trim() : null
}

/**
 * @returns {Promise<object|null>} null if route not handled locally
 */
export async function localApi(path, { method = 'GET', body, headers } = {}) {
  const user = decodeSession(getBearer(headers))
  const role = user?.role ?? 'GUEST'

  if (path === '/api/health') {
    return {
      ok: true,
      service: '26-fatigue-bid',
      host: 'https://26-fatigue-bid.softwarelink.net/',
      slug: '26-fatigue-bid',
      runtime: 'allworld-static',
      ts: new Date().toISOString(),
    }
  }

  if (path === '/api/auth/login' && method === 'POST') {
    const email = String(body?.email || '').toLowerCase()
    const row = DEMO_CREDENTIALS[email]
    if (!row || row.password !== body?.password) {
      const err = new Error('账号或密码错误')
      err.status = 401
      throw err
    }
    const token = encodeSession(row.user)
    pushAudit(row.user.id, 'AUTH_LOGIN_SUCCESS', { email, runtime: 'allworld' })
    return {
      ok: true,
      token,
      user: row.user,
      caSignMock: {
        algorithm: 'SM2-with-SM3',
        signer: row.user.displayName,
        signedAt: new Date().toISOString(),
        digest: `CA-${row.user.id}-${Date.now().toString(16)}`,
      },
    }
  }

  if (path === '/api/auth/guest' && method === 'POST') {
    const guest = {
      id: 'usr_guest_anon',
      email: 'guest@local',
      displayName: '访客 (Guest)',
      role: 'GUEST',
      organizationType: 'DOMESTIC',
    }
    return { ok: true, token: encodeSession(guest), user: guest }
  }

  if (path === '/api/auth/me' && method === 'GET') {
    if (!user) {
      const err = new Error('会话无效或已过期')
      err.status = 401
      throw err
    }
    return { ok: true, user }
  }

  if (path === '/api/auth/logout' && method === 'POST') {
    return { ok: true }
  }

  if (path === '/api/tender' && method === 'GET') {
    const saleEnd = parseDeadline('2026-08-11 16:00:00')
    const bidEnd = parseDeadline('2026-08-24 09:30:00')
    const techPoints = [
      '高刚度双轴加载机架与超高温加热环境仓（轴向动态拉压 + 同轴高频双向扭转）',
      '硬件控制器与多轴疲劳测试控制分析软件必须同一制造商原装同源开发',
      '多通道亚毫秒级高速采样、动态相位解耦与 DIC / 高温引伸计闭环接口',
      '关境外投标人须具备外贸合同签署与合规收汇能力，并提供近3个月银行资信证明',
    ]
    return {
      ok: true,
      role,
      tender: {
        projectNo: '0730-269010010004/01',
        title: '高温拉扭复合疲劳试验系统',
        buyerName: '中航金属材料理化检测科技有限公司',
        agencyName: '中航招采科技（北京）有限公司',
        deliveryLocation: '贵州省安顺市西秀区黄果树大街322号安大厂内',
        deliveryPeriodMonths: 8,
        docFeeRmb: 500,
        docFeeUsd: 75,
        status: 'SALE_OPEN',
        summary: '国际公开招标采购壹套高温拉扭复合疲劳试验系统，要求软硬件同源开发，支持高温多轴力热耦合测试。',
      },
      countdown: {
        documentSale: countdown(saleEnd),
        bidDeadline: countdown(bidEnd),
      },
      milestones: [
        { key: 'publish', label: '公告发布', at: '2026-08-01 14:37:31', done: true },
        { key: 'doc_sale_start', label: '招标文件发售开始', at: '2026-08-01 09:00:00', done: true },
        { key: 'doc_sale_end', label: '招标文件发售截止', at: '2026-08-11 16:00:00', done: Date.now() > saleEnd },
        { key: 'bid_deadline', label: '投标截止 / 开标', at: '2026-08-24 09:30:00', done: Date.now() > bidEnd },
        { key: 'delivery', label: '合同签订后交货期', at: '签约后 8 个月内', done: false },
      ],
      techPoints: role === 'GUEST' ? techPoints.slice(0, 2) : techPoints,
      changeStream: role === 'GUEST'
        ? [{ at: '2026-08-01', note: '公告公开发布（公开摘要）' }]
        : [
            { at: '2026-08-01 14:37:31', note: '国际公开招标公告发布' },
            { at: '2026-08-01 09:00:00', note: '招标文件开始发售（人民币500 / 美元75）' },
            { at: '2026-08-11 16:00:00', note: '招标文件发售截止节点' },
            { at: '2026-08-24 09:30:00', note: '投标截止与开标节点' },
          ],
    }
  }

  if (path === '/api/tender/export' && method === 'GET') {
    if (role !== 'LAB_ENGINEER') {
      const err = new Error('权限不足：当前角色无法访问此资源')
      err.status = 403
      throw err
    }
    return { ok: true, exportedAt: new Date().toISOString(), items: [{ project_no: '0730-269010010004/01' }], runtime: 'allworld' }
  }

  if (path === '/api/fatigue/presets' && method === 'GET') {
    const isGuest = role === 'GUEST'
    return {
      ok: true,
      mode: isGuest ? 'basic_readonly' : role === 'LAB_ENGINEER' ? 'expert' : 'full',
      presets: isGuest
        ? PRESETS.map((p) => ({
            id: p.id,
            preset_name: p.preset_name,
            material_grade: p.material_grade,
            temp_celsius: p.temp_celsius,
            axial_load_kn: p.axial_load_kn,
            frequency_hz: p.frequency_hz,
          }))
        : PRESETS,
    }
  }

  if (path === '/api/fatigue/calc' && method === 'POST') {
    const isGuest = role === 'GUEST'
    const axialLoadKn = Number(body.axialLoadKn)
    const torsionTorqueNm = Number(body.torsionTorqueNm)
    const frequencyHz = Number(body.frequencyHz)
    const phaseShiftDeg = Number(body.phaseShiftDeg)
    const tempCelsius = Number(body.tempCelsius)
    const result = computeDualAxisWaveform({
      axialLoadKn,
      torsionTorqueNm,
      frequencyHz,
      phaseShiftDeg: isGuest ? 0 : phaseShiftDeg,
      tempCelsius: isGuest ? Math.min(tempCelsius, 650) : tempCelsius,
      samples: isGuest ? 180 : 720,
    })
    return {
      ok: true,
      role,
      input: { axialLoadKn, torsionTorqueNm, frequencyHz, phaseShiftDeg, tempCelsius },
      result,
      dllMapping: role === 'LAB_ENGINEER'
        ? {
            driver: 'FatigueCtrl_C_DLL_v3.so',
            exports: ['FAT_InitAxis', 'FAT_SetWaveform', 'FAT_ArmPhaseLock', 'FAT_StartDualAxis'],
            channelMap: { CH0: 'Fz_axial_kN', CH1: 'Mz_torsion_Nm', CH2: 'T_furnace_C' },
          }
        : undefined,
    }
  }

  if (path === '/api/fatigue/export' && method === 'POST') {
    if (role !== 'LAB_ENGINEER') {
      const err = new Error('权限不足：当前角色无法访问此资源')
      err.status = 403
      throw err
    }
    const spectrum = computeDualAxisWaveform(body)
    return { ok: true, r2Key: `local/${Date.now()}.json`, spectrum, runtime: 'allworld' }
  }

  if (path === '/api/qualification/check' && method === 'POST') {
    if (!['AUDITOR', 'LAB_ENGINEER'].includes(role)) {
      const err = new Error('权限不足：当前角色无法访问此资源')
      err.status = 403
      throw err
    }
    const evaluation = evaluateQualification({
      bidderName: body.bidderName,
      bidderType: body.bidderType,
      hasManufacturerAuth: !!body.hasManufacturerAuth,
      hasBankCreditCert: !!body.hasBankCreditCert,
      hasForeignTradeContractCapacity: !!body.hasForeignTradeContractCapacity,
      hasCreditChinaViolation: !!body.hasCreditChinaViolation,
      bankCertMonths: body.bankCertMonths ?? 3,
    })
    const id = `qual_${Date.now().toString(36)}`
    const records = loadJson(QUAL_STORE_KEY, [])
    records.unshift({
      id,
      bidder_name: body.bidderName,
      bidder_type: body.bidderType,
      compliance_score: evaluation.complianceScore,
      updated_at: new Date().toISOString(),
    })
    saveJson(QUAL_STORE_KEY, records.slice(0, 100))
    pushAudit(user?.id, 'QUALIFICATION_CHECK', { id, score: evaluation.complianceScore })
    return { ok: true, id, evaluation, archived: role === 'LAB_ENGINEER' }
  }

  if (path === '/api/qualification/records' && method === 'GET') {
    if (!['AUDITOR', 'LAB_ENGINEER'].includes(role)) {
      const err = new Error('权限不足：当前角色无法访问此资源')
      err.status = 403
      throw err
    }
    return { ok: true, records: loadJson(QUAL_STORE_KEY, []) }
  }

  if (path === '/api/qualification/criteria' && method === 'GET') {
    if (!['AUDITOR', 'LAB_ENGINEER'].includes(role)) {
      const err = new Error('权限不足：当前角色无法访问此资源')
      err.status = 403
      throw err
    }
    return {
      ok: true,
      criteria: [
        { key: 'manufacturerAuth', label: '制造商授权 / 软硬件同源证明', weight: 0.25, required: true },
        { key: 'bankCredit', label: '近3个月银行资信证明', weight: 0.25, required: true },
        { key: 'foreignTrade', label: '关境外外贸合同与合规收汇能力', weight: 0.2, requiredFor: 'OVERSEAS' },
        { key: 'creditIntegrity', label: '非失信被执行人（信用中国）', weight: 0.2, required: true },
        { key: 'documentCompleteness', label: '投标要件完备度', weight: 0.1, required: true },
      ],
      editable: role === 'LAB_ENGINEER',
    }
  }

  if (path === '/api/admin/configs' && method === 'GET') {
    if (!['AUDITOR', 'LAB_ENGINEER'].includes(role)) {
      const err = new Error('权限不足：当前角色无法访问此资源')
      err.status = 403
      throw err
    }
    const defaults = [
      { config_key: 'ENABLE_DUAL_AXIS_CANVAS', config_value: 'true', description: '启用双轴拉扭动态波形实时渲染引擎', is_feature_flag: 1 },
      { config_key: 'ENABLE_FOREIGN_TRADE_AUDIT_MODE', config_value: 'true', description: '启用关境外投标主体跨境结汇与外贸履约审核模式', is_feature_flag: 1 },
      { config_key: 'PROJECT_SLUG', config_value: '26-fatigue-bid', description: '项目唯一标识', is_feature_flag: 0 },
      { config_key: 'DEPLOYMENT_HOST', config_value: 'https://26-fatigue-bid.softwarelink.net/', description: '主控上线域名', is_feature_flag: 0 },
    ]
    const saved = loadJson(CONFIG_STORE_KEY, {})
    return {
      ok: true,
      configs: defaults.map((c) => ({
        ...c,
        config_value: saved[c.config_key] ?? c.config_value,
        updated_at: new Date().toISOString(),
      })),
    }
  }

  if (path === '/api/admin/configs' && method === 'PUT') {
    if (role !== 'LAB_ENGINEER') {
      const err = new Error('权限不足：当前角色无法访问此资源')
      err.status = 403
      throw err
    }
    const saved = loadJson(CONFIG_STORE_KEY, {})
    saved[body.config_key] = body.config_value
    saveJson(CONFIG_STORE_KEY, saved)
    pushAudit(user?.id, 'CONFIG_UPDATE', body)
    return { ok: true }
  }

  if (path === '/api/admin/audit-logs' && method === 'GET') {
    if (!['AUDITOR', 'LAB_ENGINEER'].includes(role)) {
      const err = new Error('权限不足：当前角色无法访问此资源')
      err.status = 403
      throw err
    }
    return { ok: true, logs: loadJson(AUDIT_STORE_KEY, []) }
  }

  if (path === '/api/admin/seed-reset' && method === 'POST') {
    if (role !== 'LAB_ENGINEER') {
      const err = new Error('权限不足：当前角色无法访问此资源')
      err.status = 403
      throw err
    }
    saveJson(QUAL_STORE_KEY, [])
    pushAudit(user?.id, 'SEED_RESET', { note: 'local qualification cleared' })
    return { ok: true, message: '本地资格自检归档已重置（allworld 静态模式）' }
  }

  return null
}
